import React from "react";

import {CheckIcon} from "@heroicons/react/20/solid";
import {Decimal} from "decimal.js";
import {produce} from "immer";
import {DayPicker} from "react-day-picker";

import * as auth from "@client/features/auth";
import * as dialogs from "@client/features/dialogs";
import {annotateWithExercises} from "@client/features/workouts";

import * as constants from "@client/constants";
import * as dates from "@client/dates";
import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";
import * as utils from "@client/utils";

import {ExerciseSetsPreview} from "@client/components/ExerciseSet";
import {Icon} from "@client/components/Icon";
import {ContentModal} from "@client/components/Modal";
import {processResponse} from "@client/components/Workout";
import * as journeys from "@client/journeys";
import {Register} from "@client/site/components";
import * as forms from "@client/site/forms";
import {useAppDispatch} from "@client/store";
import {classNames, models, server} from "@reactivated";

const Card = {isComplete: ""} as const;

type SchemeSet = {
    reps: string;
    weight?: string;
    type?: "AMRAP" | "WORKING" | "WARM_UP";
};

type Scheme = (() => Array<Array<SchemeSet>>) | Array<Array<SchemeSet>>;

type FiveThreeOneTemplate =
    | "BORING_BUT_BIG"
    | "BORING_BUT_STRONG"
    | "FIRST_SET_LAST"
    | "SECOND_SET_LAST";

const schemes: Record<
    | "WARM_UP"
    | "QUICK_WARMUP"
    | "TM"
    | "DELOAD"
    | "FIVE_BY_TEN"
    | "FIVE_THREE_ONE_LEADER"
    | "FIVE_THREE_ONE_ANCHOR"
    | FiveThreeOneTemplate,
    Scheme
> = {
    WARM_UP: [
        [
            {reps: "5", weight: ".40", type: "WARM_UP"},
            {reps: "5", weight: ".50", type: "WARM_UP"},
            {reps: "3", weight: ".60", type: "WARM_UP"},
        ],
        [
            {reps: "5", weight: ".40", type: "WARM_UP"},
            {reps: "5", weight: ".50", type: "WARM_UP"},
            {reps: "3", weight: ".60", type: "WARM_UP"},
        ],
        [
            {reps: "5", weight: ".40", type: "WARM_UP"},
            {reps: "5", weight: ".50", type: "WARM_UP"},
            {reps: "3", weight: ".60", type: "WARM_UP"},
        ],
    ],
    QUICK_WARMUP: [
        [
            {reps: "5", weight: ".40", type: "WARM_UP"},
            {reps: "3", weight: ".50", type: "WARM_UP"},
        ],
        [
            {reps: "5", weight: ".40", type: "WARM_UP"},
            {reps: "3", weight: ".50", type: "WARM_UP"},
        ],
        [
            {reps: "5", weight: ".40", type: "WARM_UP"},
            {reps: "3", weight: ".50", type: "WARM_UP"},
        ],
    ],
    DELOAD: [
        [
            {reps: "5", weight: ".40", type: "WARM_UP"},
            {reps: "5", weight: ".50", type: "WARM_UP"},
            {reps: "3", weight: ".60", type: "WARM_UP"},
            {reps: "5", weight: ".70"},
            {reps: "3", weight: ".80"},
            {reps: "1", weight: ".90"},
            {reps: "1", weight: "1"},
        ],
    ],
    TM: [
        [{reps: "1", weight: "1"}],
        [{reps: "1", weight: "1"}],
        [{reps: "1", weight: "1"}],
    ],
    FIVE_THREE_ONE_LEADER: [
        [
            {reps: "5", weight: ".65"},
            {reps: "5", weight: ".75"},
            {reps: "5", weight: ".85"},
        ],
        [
            {reps: "5", weight: ".70"},
            {reps: "5", weight: ".80"},
            {reps: "5", weight: ".90"},
        ],
        [
            {reps: "5", weight: ".75"},
            {reps: "5", weight: ".85"},
            {reps: "5", weight: ".95"},
        ],
    ],
    FIVE_THREE_ONE_ANCHOR: [
        [
            {reps: "5", weight: ".65"},
            {reps: "5", weight: ".75"},
            {reps: "5", weight: ".85", type: "AMRAP"},
        ],
        [
            {reps: "3", weight: ".70"},
            {reps: "3", weight: ".80"},
            {reps: "3", weight: ".90", type: "AMRAP"},
        ],
        [
            {reps: "5", weight: ".75"},
            {reps: "3", weight: ".85"},
            {reps: "1", weight: ".95", type: "AMRAP"},
        ],
    ],
    BORING_BUT_BIG: () =>
        [...Array(3).keys()].map(() => [
            {reps: "10", weight: ".50"},
            {reps: "10", weight: ".50"},
            {reps: "10", weight: ".50"},
            {reps: "10", weight: ".50"},
            {reps: "10", weight: ".50"},
        ]),
    BORING_BUT_STRONG: [
        [
            {reps: "5", weight: ".65"},
            {reps: "5", weight: ".65"},
            {reps: "5", weight: ".65"},
            {reps: "5", weight: ".65"},
            {reps: "5", weight: ".65"},
            {reps: "5", weight: ".65"},
            {reps: "5", weight: ".65"},
            {reps: "5", weight: ".65"},
            {reps: "5", weight: ".65"},
            {reps: "5", weight: ".65"},
        ],
        [
            {reps: "5", weight: ".70"},
            {reps: "5", weight: ".70"},
            {reps: "5", weight: ".70"},
            {reps: "5", weight: ".70"},
            {reps: "5", weight: ".70"},
            {reps: "5", weight: ".70"},
            {reps: "5", weight: ".70"},
            {reps: "5", weight: ".70"},
            {reps: "5", weight: ".70"},
            {reps: "5", weight: ".70"},
        ],
        [
            {reps: "5", weight: ".75"},
            {reps: "5", weight: ".75"},
            {reps: "5", weight: ".75"},
            {reps: "5", weight: ".75"},
            {reps: "5", weight: ".75"},
            {reps: "5", weight: ".75"},
            {reps: "5", weight: ".75"},
            {reps: "5", weight: ".75"},
            {reps: "5", weight: ".75"},
            {reps: "5", weight: ".75"},
        ],
    ],

    FIRST_SET_LAST: [
        [
            {reps: "5", weight: ".65"},
            {reps: "5", weight: ".65"},
            {reps: "5", weight: ".65"},
            {reps: "5", weight: ".65"},
            {reps: "5", weight: ".65"},
        ],
        [
            {reps: "5", weight: ".70"},
            {reps: "5", weight: ".70"},
            {reps: "5", weight: ".70"},
            {reps: "5", weight: ".70"},
            {reps: "5", weight: ".70"},
        ],
        [
            {reps: "5", weight: ".75"},
            {reps: "5", weight: ".75"},
            {reps: "5", weight: ".75"},
            {reps: "5", weight: ".75"},
            {reps: "5", weight: ".75"},
        ],
    ],
    SECOND_SET_LAST: [
        [
            {reps: "5", weight: ".75"},
            {reps: "5", weight: ".75"},
            {reps: "5", weight: ".75"},
            {reps: "5", weight: ".75"},
            {reps: "5", weight: ".75"},
        ],
        [
            {reps: "5", weight: ".80"},
            {reps: "5", weight: ".80"},
            {reps: "5", weight: ".80"},
            {reps: "5", weight: ".80"},
            {reps: "5", weight: ".80"},
        ],
        [
            {reps: "5", weight: ".85"},
            {reps: "5", weight: ".85"},
            {reps: "5", weight: ".85"},
            {reps: "5", weight: ".85"},
            {reps: "5", weight: ".85"},
        ],
    ],
    FIVE_BY_TEN: [
        [{reps: "10"}, {reps: "10"}, {reps: "10"}, {reps: "10"}, {reps: "10"}],
        [{reps: "10"}, {reps: "10"}, {reps: "10"}, {reps: "10"}, {reps: "10"}],
        [{reps: "10"}, {reps: "10"}, {reps: "10"}, {reps: "10"}, {reps: "10"}],
    ],
};

// Linear progression schemes - these define set/rep patterns for linear programs
// Weights are calculated via (max × percentage) + (week × progression)
type LinearScheme = {
    sets: number;
    reps: number;
    percentage: string; // Decimal percentage of max (e.g., "0.85" for 85%)
    type?: "AMRAP" | "WORKING";
};

const linearSchemes: Record<string, LinearScheme> = {
    MAIN_3X5: {sets: 3, reps: 5, percentage: "0.85"}, // Heavy - 85% of max
    MAIN_1X5: {sets: 1, reps: 5, percentage: "0.85"}, // Heavy - 85% of max
    SUPPLEMENTAL_5X5: {sets: 5, reps: 5, percentage: "0.75"}, // Volume - 75% of max
};

type LiftDay = Array<{
    name: string;
    type: UUID;
    schemes: Array<Array<keyof typeof schemes | null>>;
}>;

type Maxes = {
    [uuid: string]: {
        weight: string | null;
        reps: number | null;
        smallest_plate: string | null;
    } | null;
};

type ProgramScheme = Array<LiftDay>;

function calculate531(
    programScheme: ProgramScheme,
    maxes: Maxes,
    settings: {isImperial: boolean},
) {
    const programsByLift = programScheme.map((liftDay) => {
        const individualLiftDayAcrossWeeks = liftDay.map((lift) => {
            const exercisesPerScheme = lift.schemes.map((schemeName) => {
                const scheme = schemeName.reduce<SchemeSet[][]>((acc, scheme) => {
                    if (scheme == null) {
                        return acc;
                    }

                    if (typeof schemes[scheme] == "function") {
                        return acc.concat(schemes[scheme]());
                    }
                    return acc.concat(schemes[scheme]);
                }, []);

                return scheme.map((sets) => {
                    return sets.map((set) => {
                        const liftMax = maxes[lift.type];
                        const smallestPlate = new Decimal(
                            liftMax?.smallest_plate ??
                                (settings.isImperial ? "2.5" : "1"),
                        );

                        const weight =
                            liftMax?.weight != null && set.weight != null
                                ? utils
                                      .roundNumberToNearest(
                                          new Decimal(liftMax.weight)
                                              .times(set.weight)
                                              .dividedBy(
                                                  settings.isImperial
                                                      ? "0.45359237"
                                                      : "1",
                                              ),
                                          smallestPlate.times(2),
                                      )
                                      .times(settings.isImperial ? "0.45359237" : "1")
                                      .toString()
                                : null;

                        return {
                            type:
                                ("type" in set ? set.type : null) ??
                                ("WORKING" as const),
                            // That way we can use ReturnType<calculate> for sharing preview logic.
                            reps: set.reps.toString(),
                            weight,
                            uuid: crypto.randomUUID(),
                        };
                    });
                });
            });

            const setsPerSchemeCount = exercisesPerScheme.reduce(
                (max, arr) => Math.max(max, arr.length),
                0,
            );

            const weeks = [...Array(setsPerSchemeCount).keys()].map((index) => {
                return exercisesPerScheme
                    .map((exercise) => {
                        const sets = [exercise[index], null][0];

                        if (sets == null) {
                            return null;
                        }
                        return sets;
                    })
                    .filter((item): item is NonNullable<typeof item> => item != null)
                    .flat();
            });

            const exercise = {
                name: lift.name,
                type: lift.type,
            };

            return {exercise, weeks};
        });

        // Some lifts aren't present in the last weeks, like deloads.
        const weeksPerLiftCount = individualLiftDayAcrossWeeks.reduce(
            (max, arr) => Math.max(max, arr.weeks.length),
            0,
        );

        return [...Array(weeksPerLiftCount).keys()]
            .map((index) => {
                return individualLiftDayAcrossWeeks
                    .map((liftDayExercise) => {
                        const sets = [liftDayExercise.weeks[index], null][0];

                        if (sets == null) {
                            return null;
                        }
                        const uuid = crypto.randomUUID();

                        return {
                            exercise: {...liftDayExercise.exercise, uuid},
                            sets: sets.map((set, order) => ({
                                ...set,
                                order,
                                exercise: uuid,
                            })),
                        };
                    })
                    .filter((item): item is NonNullable<typeof item> => item != null);
            })
            .map((workoutExercises) => ({
                uuid: crypto.randomUUID(),
                name: workoutExercises[0].exercise.name,
                exercises: workoutExercises,
            }));
    });

    const groupsCount = programsByLift.reduce(
        (max, arr) => Math.max(max, arr.length),
        0,
    );

    return [...Array(groupsCount).keys()]
        .map((index) => {
            return programsByLift.map((program) => {
                return program[index] ?? false;
            });
        })
        .map((groupWorkouts) => ({workouts: groupWorkouts, uuid: crypto.randomUUID()}));
}

function calculateLinearWeight(
    maxKg: string,
    percentage: string,
    incrementKg: string,
    weekIndex: number,
    smallestPlateKg: string | null,
    isImperial: boolean,
): string {
    // Calculate base weight from max * percentage
    const max = new Decimal(maxKg);
    const base = max.times(percentage);

    // Add weekly progression
    const increment = new Decimal(incrementKg).times(weekIndex);
    const total = base.plus(increment);

    // Convert to display units for rounding
    const displayWeight = total.dividedBy(isImperial ? "0.45359237" : "1");

    // Convert smallest plate to display units, then multiply by 2 (both sides of bar)
    const smallestPlateDisplay = new Decimal(
        smallestPlateKg ?? (isImperial ? "1.133980925" : "1"),
    ).dividedBy(isImperial ? "0.45359237" : "1");
    const plateSize = smallestPlateDisplay.times(2);
    const rounded = utils.roundNumberToNearest(displayWeight, plateSize);

    // Convert back to kg for storage
    return rounded.times(isImperial ? "0.45359237" : "1").toString();
}

function generateLinearWarmups(
    workingWeightKg: string,
    settings: {isImperial: boolean},
    includeBar: boolean = true,
): Array<{type: "WARM_UP"; reps: string; weight: string; uuid: UUID}> {
    const working = new Decimal(workingWeightKg);
    const warmups: Array<{type: "WARM_UP"; reps: string; weight: string; uuid: UUID}> =
        [];

    // Bar warmup: 45 lb (20.41165665 kg) or 20 kg
    const barWeightKg = settings.isImperial
        ? new Decimal("45").times("0.45359237")
        : new Decimal("20");

    // Standard warmup: 50%, 70%, 90% of working weight
    const percentages = ["0.50", "0.65", "0.80"];
    const reps = ["5", "5", "3"];

    // Round each warmup target to nearest 5, then round the jump to nearest 10 for clean increments
    // Example imperial: 45→55→75→95 (jumps: 10, 20, 20) | Example metric: 20→30→45→60 (jumps: 10, 15→20, 15→20)
    const barDisplayWeight = barWeightKg.dividedBy(
        settings.isImperial ? "0.45359237" : "1",
    );

    const calculatedWarmups: Array<{
        type: "WARM_UP";
        reps: string;
        weight: string;
        uuid: UUID;
    }> = [];

    for (let index = 0; index < percentages.length; index++) {
        const pct = percentages[index];
        const warmupWeight = working.times(pct);
        const displayWeight = warmupWeight.dividedBy(
            settings.isImperial ? "0.45359237" : "1",
        );

        // Round target to nearest 5
        const roundedTarget = utils.roundNumberToNearest(
            displayWeight,
            new Decimal("5"),
        );

        // Calculate jump from previous warmup (or bar if first)
        const previousDisplay =
            index === 0
                ? barDisplayWeight
                : new Decimal(calculatedWarmups[index - 1].weight).dividedBy(
                      settings.isImperial ? "0.45359237" : "1",
                  );

        const jump = roundedTarget.minus(previousDisplay);

        // Round jump to nearest 10
        const roundedJump = utils.roundNumberToNearest(jump, new Decimal("10"));

        // Final warmup = previous + rounded jump (in display units)
        const finalDisplay = previousDisplay.plus(roundedJump);

        // Convert back to kg for storage
        const finalWeight = finalDisplay
            .times(settings.isImperial ? "0.45359237" : "1")
            .toString();

        calculatedWarmups.push({
            type: "WARM_UP" as const,
            reps: reps[index],
            weight: finalWeight,
            uuid: crypto.randomUUID(),
        });
    }

    // Filter out warmups that are <= bar weight or within 10% of bar weight
    const barWeightWithBuffer = barWeightKg.times("1.1");
    const filteredWarmups = calculatedWarmups.filter((warmup) => {
        const warmupWeight = new Decimal(warmup.weight);
        return warmupWeight.greaterThan(barWeightWithBuffer);
    });

    // Add bar warmup if requested
    if (includeBar) {
        warmups.push({
            type: "WARM_UP" as const,
            reps: "5",
            weight: barWeightKg.toString(),
            uuid: crypto.randomUUID(),
        });
    }

    // Add filtered warmups
    warmups.push(...filteredWarmups);

    return warmups;
}

type LinearLiftConfig = {
    type: UUID;
    name: string;
    opposite?: string;
    oppositeType?: UUID;
    mainScheme: keyof typeof linearSchemes;
    supplementalScheme?: keyof typeof linearSchemes;
    progression_per_week: string;
    smallest_plate: string;
    includeBarWarmup?: boolean;
    oppositeFirst?: boolean; // When true, opposite lift goes first with full warmups
};

function calculateLinearProgram(
    linearConfigs: Array<LinearLiftConfig>,
    weeks: number,
    maxes: Maxes,
    settings: {isImperial: boolean},
    accessories: Array<Array<{name: string; type: UUID} | undefined>>,
): ReturnType<typeof calculate531> {
    // Pre-calculate warmups for each lift based on week 1 working weight (stable across all weeks)
    const warmupsMap = new Map<
        UUID,
        Array<{type: "WARM_UP"; reps: string; weight: string; uuid: UUID}>
    >();

    for (const lift of linearConfigs) {
        const mainScheme = linearSchemes[lift.mainScheme];
        const maxKg = maxes[lift.type]?.weight ?? "0";
        const progressionKg = settings.isImperial
            ? new Decimal(lift.progression_per_week).times("0.45359237").toString()
            : lift.progression_per_week;
        const smallestPlateKg = settings.isImperial
            ? new Decimal(lift.smallest_plate).times("0.45359237").toString()
            : lift.smallest_plate;

        // Calculate week 1 working weight (weekIndex = 0)
        const week1WorkingWeight = calculateLinearWeight(
            maxKg,
            mainScheme.percentage,
            progressionKg,
            0, // Week 1
            smallestPlateKg,
            settings.isImperial,
        );

        // Generate warmups once for this lift
        const warmups = generateLinearWarmups(
            week1WorkingWeight,
            settings,
            lift.includeBarWarmup ?? true,
        );
        warmupsMap.set(lift.type, warmups);
    }

    return [...Array(weeks).keys()].map((weekIndex) => {
        const workouts = linearConfigs.map((lift, liftIndex) => {
            const exercises = [];
            const mainExerciseUUID = crypto.randomUUID();

            // Get lift configuration (values are in display units)
            const mainScheme = linearSchemes[lift.mainScheme];

            // Get max for this lift
            const maxKg = maxes[lift.type]?.weight ?? "0";

            // Convert from display units to kg for calculations
            const progressionKg = settings.isImperial
                ? new Decimal(lift.progression_per_week).times("0.45359237").toString()
                : lift.progression_per_week;
            const smallestPlateKg = settings.isImperial
                ? new Decimal(lift.smallest_plate).times("0.45359237").toString()
                : lift.smallest_plate;

            // Calculate working weight for this week (max × percentage + progression × weekIndex)
            const workingWeight = calculateLinearWeight(
                maxKg,
                mainScheme.percentage,
                progressionKg,
                weekIndex,
                smallestPlateKg,
                settings.isImperial,
            );

            // Determine if we're swapping exercise order
            const shouldSwapOrder =
                lift.oppositeFirst == true && lift.oppositeType != null;

            // 1. Get pre-calculated warmups (stable across weeks)
            const baseWarmups = warmupsMap.get(lift.type) ?? [];
            // Create new UUIDs for this week's warmup instances
            const warmups = baseWarmups.map((warmup) => ({
                ...warmup,
                uuid: crypto.randomUUID(),
            }));

            // 2. Main working sets using scheme
            // Odd weeks (0, 2, 4...): TM single, Even weeks (1, 3, 5...): AMRAP on last set
            const isAmrapWeek = weekIndex % 2 === 1;
            const isTmWeek = weekIndex % 2 === 0;

            // 3. TM singles (on even weeks: 0, 2, 4...)
            const tmSingles: Array<{
                type: "WORKING";
                reps: string;
                weight: string;
                uuid: UUID;
                order: number;
                exercise: UUID;
            }> = [];

            if (isTmWeek) {
                const tmWeight = new Decimal(maxes[lift.type]?.weight ?? "0");
                const workingWeightDecimal = new Decimal(workingWeight);

                // Calculate the gap between working weight and TM
                const gap = tmWeight.minus(workingWeightDecimal);

                // Create 3 singles building up to TM
                // Single 1: Working weight
                // Single 2 & 3: Intermediate weights
                // Single 4: TM
                const increments = 2;
                for (let i = 0; i <= increments; i++) {
                    const fraction = new Decimal(i).dividedBy(increments);
                    const singleWeight = workingWeightDecimal.plus(gap.times(fraction));

                    // Round to nearest plate increment
                    const displayWeight = singleWeight.dividedBy(
                        settings.isImperial ? "0.45359237" : "1",
                    );
                    const plateSize = new Decimal(smallestPlateKg)
                        .dividedBy(settings.isImperial ? "0.45359237" : "1")
                        .times(2);
                    const rounded = utils.roundNumberToNearest(
                        displayWeight,
                        plateSize,
                    );
                    const finalWeight = rounded
                        .times(settings.isImperial ? "0.45359237" : "1")
                        .toString();

                    tmSingles.push({
                        type: "WORKING" as const,
                        reps: "1",
                        weight: finalWeight,
                        uuid: crypto.randomUUID(),
                        order: warmups.length + i,
                        exercise: mainExerciseUUID,
                    });
                }
            }

            const mainSets = [...Array(mainScheme.sets).keys()].map((setIndex) => ({
                type:
                    setIndex === mainScheme.sets - 1 && isAmrapWeek
                        ? ("AMRAP" as const)
                        : ("WORKING" as const),
                reps: mainScheme.reps.toString(),
                weight: workingWeight,
                uuid: crypto.randomUUID(),
                order: warmups.length + tmSingles.length + setIndex,
                exercise: mainExerciseUUID,
            }));

            // Combine all sets for main exercise
            const allMainSets = [
                ...warmups.map((set, idx) => ({
                    ...set,
                    order: idx,
                    exercise: mainExerciseUUID,
                })),
                ...tmSingles, // TM singles go before main sets on TM weeks
                ...mainSets,
            ];

            const mainExercise = {
                exercise: {name: lift.name, type: lift.type, uuid: mainExerciseUUID},
                sets: allMainSets,
            };

            // 4. Supplemental lift (opposite lift using supplemental scheme)
            if (lift.oppositeType != null && lift.supplementalScheme != null) {
                const oppositeLift = linearConfigs.find(
                    (l) => l.type === lift.oppositeType,
                );
                if (oppositeLift) {
                    const suppExerciseUUID = crypto.randomUUID();
                    const suppScheme = linearSchemes[lift.supplementalScheme];

                    // Get max for opposite lift
                    const suppMaxKg = maxes[oppositeLift.type]?.weight ?? "0";

                    // Convert supplemental lift values from display units to kg
                    const suppProgressionKg = settings.isImperial
                        ? new Decimal(oppositeLift.progression_per_week)
                              .times("0.45359237")
                              .toString()
                        : oppositeLift.progression_per_week;

                    // Use the main lift's smallest plate for the supplemental lift
                    // (so you only need to bring one set of plates to the gym)
                    const suppSmallestPlateKg = smallestPlateKg; // Use main lift's plate

                    // Calculate supplemental working weight (max × percentage + progression × weekIndex)
                    const suppWorkingWeight = calculateLinearWeight(
                        suppMaxKg,
                        suppScheme.percentage,
                        suppProgressionKg,
                        weekIndex,
                        suppSmallestPlateKg,
                        settings.isImperial,
                    );

                    // Get pre-calculated warmups for supplemental lift (stable across weeks)
                    // Keep bar + skip 1st calculated (50%) + keep 2nd/3rd (70%/90%) with 3 reps each
                    const baseSuppWarmups = warmupsMap.get(oppositeLift.type) ?? [];
                    const shortenedWarmups =
                        baseSuppWarmups.length > 0
                            ? [baseSuppWarmups[0], ...baseSuppWarmups.slice(2)]
                            : [];
                    const suppWarmups = shortenedWarmups.map((warmup, index) => ({
                        ...warmup,
                        reps: index === 0 ? warmup.reps : "3", // Bar keeps 5 reps, others use 3
                        uuid: crypto.randomUUID(),
                    }));
                    const suppSets = [...Array(suppScheme.sets).keys()].map(
                        (setIndex) => ({
                            type: "WORKING" as const,
                            reps: suppScheme.reps.toString(),
                            weight: suppWorkingWeight,
                            uuid: crypto.randomUUID(),
                            order: suppWarmups.length + setIndex,
                            exercise: suppExerciseUUID,
                        }),
                    );

                    const supplementalExercise = {
                        exercise: {
                            name: oppositeLift.name,
                            type: oppositeLift.type,
                            uuid: suppExerciseUUID,
                        },
                        sets: [
                            ...suppWarmups.map((set, idx) => ({
                                ...set,
                                order: idx,
                                exercise: suppExerciseUUID,
                            })),
                            ...suppSets,
                        ],
                    };

                    // Add exercises in the correct order based on oppositeFirst flag
                    if (shouldSwapOrder == true) {
                        exercises.push(supplementalExercise);
                        exercises.push(mainExercise);
                    } else {
                        exercises.push(mainExercise);
                        exercises.push(supplementalExercise);
                    }
                } else {
                    // No supplemental exercise, just add main
                    exercises.push(mainExercise);
                }
            } else {
                // No opposite lift configured, just add main exercise
                exercises.push(mainExercise);
            }

            // 5. Accessory exercises (5x10 scheme)
            const accessoryExercises = accessories[liftIndex]
                .filter((item): item is NonNullable<typeof item> => item != null)
                .map((accessory) => {
                    const accessoryExerciseUUID = crypto.randomUUID();
                    const sets = [...Array(5).keys()].map((setIndex) => ({
                        type: "WORKING" as const,
                        reps: "10",
                        weight: null,
                        uuid: crypto.randomUUID(),
                        order: setIndex,
                        exercise: accessoryExerciseUUID,
                    }));

                    return {
                        exercise: {
                            name: accessory.name,
                            type: accessory.type,
                            uuid: accessoryExerciseUUID,
                        },
                        sets,
                    };
                });

            exercises.push(...accessoryExercises);

            return {uuid: crypto.randomUUID(), name: lift.name, exercises};
        });

        return {uuid: crypto.randomUUID(), workouts};
    });
}

interface WorkoutProgramProps {
    exercise_types: server.journal.workouts.ExerciseType.output[];
    exercise_maxes: server.journal.workouts.ExerciseMax.output[];
    profile: server.core.rpc.Profile.output | null;
}

export const ExercisePreview = (props: {
    isImperial: boolean;
    extra?: React.ReactNode;
    doNotGroup?: boolean;
    exerciseCategory: server.journal.workouts.ExerciseType.output["category"];
    exercise: ReturnType<
        typeof calculate531
    >[number]["workouts"][number]["exercises"][number];
}) => {
    const isImperial = props.isImperial;

    const groupedSets = utils.groupConsecutiveEqualElements(
        props.exercise.sets,
        (left, right) => {
            return (
                props.doNotGroup != true &&
                left.reps == right.reps &&
                left.type == right.type &&
                left.weight == right.weight
            );
        },
    );

    // TODO: Duration is wrong because it might not always be seconds.
    const suffix =
        props.exerciseCategory == "WEIGHT"
            ? "reps"
            : props.exerciseCategory == "DURATION"
              ? "seconds"
              : "minutes";

    return (
        <div>
            <div className="flex items-center gap-4">
                <h4 className="flex-1 truncate text-base">
                    {props.exercise.exercise.name}
                </h4>
                {props.extra}
            </div>

            {groupedSets.map((groupedSet, index) => {
                const {set, prefix} = Array.isArray(groupedSet)
                    ? {set: groupedSet[0], prefix: `${groupedSet.length} sets x `}
                    : {set: groupedSet, prefix: ""};

                if (set.weight == null) {
                    return null;
                }

                return (
                    <div
                        key={index}
                        className="
                            ml-2 text-xs text-ellipsis
                            sm:text-sm
                        "
                    >
                        {prefix}
                        {set.reps} {suffix} x{" "}
                        {isImperial
                            ? new Decimal(set.weight)
                                  .dividedBy("0.45359237")
                                  .toDecimalPlaces(1)
                                  .toString() + " lb"
                            : new Decimal(set.weight).toDecimalPlaces(1).toString() +
                              " kg"}{" "}
                        {set.type == "AMRAP" ? (
                            <span
                                className="
                                    text-[8px] font-semibold
                                    md:text-[10px]
                                "
                            >
                                AMRAP
                            </span>
                        ) : (
                            ""
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export const WorkoutProgram = (props: WorkoutProgramProps) => {
    const isImperial = forms.useIsImperial();
    const suffix = isImperial ? "lb" : "kg";

    const maxesForms = [
        {
            type: constants.EXERCISE_TYPES.BENCH,
            short: "Bench",
            name: "Bench Press",
            opposite: "Overhead Press",
            default: {
                imperial: "185",
                metric: "80",
            },
        },
        {
            type: constants.EXERCISE_TYPES.DEADLIFT,
            name: "Deadlift",
            opposite: "Squat",
            default: {
                imperial: "225",
                metric: "100",
            },
        },
        {
            type: constants.EXERCISE_TYPES.PRESS,
            short: "Press",
            name: "Overhead Press",
            opposite: "Bench Press",
            default: {
                imperial: "135",
                metric: "60",
            },
        },
        {
            type: constants.EXERCISE_TYPES.SQUAT,
            name: "Squat",
            opposite: "Deadlift",
            default: {
                imperial: "315",
                metric: "140",
            },
        },
    ].map((exercise) => ({
        ...exercise,
        form: forms.useAutoPform(server.journal.workouts.ExerciseMaxForm, {
            reps:
                props.exercise_maxes.find(
                    (max) => max.exercise_type.uuid == exercise.type,
                )?.reps ?? 3,
            weight:
                props.exercise_maxes.find(
                    (max) => max.exercise_type.uuid == exercise.type,
                )?.weight ??
                (isImperial
                    ? new Decimal(exercise.default.imperial).times("0.45359237")
                    : new Decimal(exercise.default.metric)
                ).toString(),
            smallest_plate: isImperial ? "2.5" : "1",
        }),
    }));

    const linearLiftForms = [
        {
            type: constants.EXERCISE_TYPES.BENCH,
            name: "Bench Press",
            opposite: "Overhead Press",
            oppositeType: constants.EXERCISE_TYPES.PRESS,
            mainScheme: "MAIN_3X5" as const,
            supplementalScheme: "SUPPLEMENTAL_5X5" as const,
            default: {
                progression: {imperial: "5", metric: "2.26796185"},
                smallestPlate: {imperial: "2.5", metric: "1"},
            },
        },
        {
            type: constants.EXERCISE_TYPES.DEADLIFT,
            name: "Deadlift",
            opposite: "Squat",
            oppositeType: constants.EXERCISE_TYPES.SQUAT,
            mainScheme: "MAIN_1X5" as const,
            supplementalScheme: "SUPPLEMENTAL_5X5" as const,
            includeBarWarmup: false,
            oppositeFirst: true, // Squat goes first on deadlift day
            default: {
                progression: {imperial: "10", metric: "4.5359237"},
                smallestPlate: {imperial: "2.5", metric: "1"},
            },
        },
        {
            type: constants.EXERCISE_TYPES.PRESS,
            name: "Overhead Press",
            opposite: "Bench Press",
            oppositeType: constants.EXERCISE_TYPES.BENCH,
            mainScheme: "MAIN_3X5" as const,
            supplementalScheme: "SUPPLEMENTAL_5X5" as const,
            default: {
                progression: {imperial: "5", metric: "2.26796185"},
                smallestPlate: {imperial: "2.5", metric: "1"},
            },
        },
        {
            type: constants.EXERCISE_TYPES.SQUAT,
            name: "Squat",
            mainScheme: "MAIN_3X5" as const,
            default: {
                progression: {imperial: "5", metric: "2.26796185"},
                smallestPlate: {imperial: "2.5", metric: "1"},
            },
        },
    ].map((lift) => ({
        ...lift,
        form: forms.useAutoPform(server.journal.workouts.LinearLiftConfigForm, {
            progression_per_week: isImperial
                ? lift.default.progression.imperial
                : lift.default.progression.metric,
            smallest_plate: isImperial
                ? lift.default.smallestPlate.imperial
                : lift.default.smallestPlate.metric,
        }),
    }));

    const programSettingsForm = forms.useAutoPform(
        server.journal.workouts.FiveThreeOneSettingsForm,
    );

    const linearProgramSettingsForm = forms.useAutoPform(
        server.journal.workouts.LinearProgramSettingsForm,
    );

    type ProgramMode = "TEMPLATE" | "LINEAR";
    const [programMode, setProgramMode] = React.useState<ProgramMode>("TEMPLATE");

    const [liftsPerDay, setLiftsPerDay] = React.useState([
        [props.exercise_types.find((e) => e.user_id == null && e.name == "Pull Ups")],
        [props.exercise_types.find((e) => e.user_id == null && e.name == "Dips")],
        [
            props.exercise_types.find(
                (e) => e.user_id == null && e.name == "Pendlay Row",
            ),
        ],
        [props.exercise_types.find((e) => e.user_id == null && e.name == "Leg Raises")],
    ]);

    const [linearLiftsPerDay, setLinearLiftsPerDay] = React.useState([
        [props.exercise_types.find((e) => e.user_id == null && e.name == "Pull Ups")],
        [props.exercise_types.find((e) => e.user_id == null && e.name == "Dips")],
        [
            props.exercise_types.find(
                (e) => e.user_id == null && e.name == "Pendlay Row",
            ),
        ],
        [props.exercise_types.find((e) => e.user_id == null && e.name == "Leg Raises")],
    ]);

    const workoutProgramForm = forms.useAutoPform(
        server.journal.workouts.WorkoutProgramConfigForm,
    );

    const programScheme: ProgramScheme = maxesForms.map((lift, liftIndex) => {
        const assistance: LiftDay = liftsPerDay[liftIndex]
            .filter((item): item is NonNullable<typeof item> => item != null)
            .map((exercise) => ({
                name: exercise.name,
                schemes: [["FIVE_BY_TEN"]],
                type: exercise.uuid,
            }));
        const opposite = maxesForms.find((e) => e.name == lift.opposite)!;
        const supplementalScheme = programSettingsForm.values.template;

        const workSetsAndSupplemental: LiftDay =
            programSettingsForm.values.swap_exercises == true
                ? [
                      {
                          name: lift.name,
                          type: lift.type,
                          schemes: [
                              ["WARM_UP"],
                              [
                                  programSettingsForm.values.cycle_type == "ANCHOR"
                                      ? "FIVE_THREE_ONE_ANCHOR"
                                      : "FIVE_THREE_ONE_LEADER",
                                  programSettingsForm.values.deload_week
                                      ? "DELOAD"
                                      : null,
                              ],

                              programSettingsForm.values.heavy_single ? ["TM"] : [],
                          ],
                      },
                      {
                          name: opposite.name,
                          type: opposite.type,
                          schemes: [["QUICK_WARMUP"], [supplementalScheme]],
                      },
                  ]
                : [
                      {
                          name: lift.name,
                          type: lift.type,
                          schemes: [
                              ["WARM_UP"],
                              [
                                  programSettingsForm.values.cycle_type == "ANCHOR"
                                      ? "FIVE_THREE_ONE_ANCHOR"
                                      : "FIVE_THREE_ONE_LEADER",
                                  programSettingsForm.values.deload_week
                                      ? "DELOAD"
                                      : null,
                              ],
                              programSettingsForm.values.heavy_single ? ["TM"] : [],
                              [supplementalScheme],
                          ],
                      },
                  ];

        return [
            ...workSetsAndSupplemental,
            ...(programSettingsForm.values.assistance_exercises ? assistance : []),
        ];
    });

    const maxes = maxesForms.reduce<Maxes>((acc, form) => {
        return {...acc, [form.type]: form.form.values};
    }, {});

    const [results, setResults] = React.useState<ReturnType<
        typeof calculate531
    > | null>(null);
    const [data, setData] = React.useState("");

    const resultsRef = React.useRef<HTMLDivElement | null>(null);

    const calculateResults = () => {
        if (
            programMode === "TEMPLATE" &&
            ["", null].includes(programSettingsForm.values.template)
        ) {
            return;
        }

        const calculatedResults =
            programMode === "LINEAR"
                ? (() => {
                      // Extract plain data from forms (avoid coupling to form handlers)
                      const linearConfigs: Array<LinearLiftConfig> =
                          linearLiftForms.map((lift) => ({
                              type: lift.type,
                              name: lift.name,
                              opposite: lift.opposite,
                              oppositeType: lift.oppositeType,
                              mainScheme: lift.mainScheme,
                              supplementalScheme: lift.supplementalScheme,
                              progression_per_week:
                                  lift.form.values.progression_per_week ?? "0",
                              smallest_plate: lift.form.values.smallest_plate ?? "0",
                              includeBarWarmup: lift.includeBarWarmup,
                              oppositeFirst: lift.oppositeFirst,
                          }));

                      const linearAccessories = linearLiftsPerDay.map(
                          (dayAccessories) =>
                              dayAccessories.map((accessory) =>
                                  accessory
                                      ? {name: accessory.name, type: accessory.uuid}
                                      : undefined,
                              ),
                      );

                      return calculateLinearProgram(
                          linearConfigs,
                          linearProgramSettingsForm.values.weeks ?? 3,
                          maxes,
                          {isImperial},
                          linearAccessories,
                      );
                  })()
                : calculate531(programScheme, maxes, {isImperial});

        setData(
            JSON.stringify({
                mode: programMode,
                workoutProgramForm: workoutProgramForm.values,
                programSettingsForm: programSettingsForm.values,
                linearProgramSettingsForm: linearProgramSettingsForm.values,
                linearLiftForms: linearLiftForms.map((lift) => ({
                    type: lift.type,
                    name: lift.name,
                    values: lift.form.values,
                })),
                maxes,
                liftsPerDay,
                isImperial,
            }),
        );

        setResults(calculatedResults);

        setTimeout(() => {
            resultsRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }, 200);
    };

    const [isSaving, setIsSaving] = React.useState(false);
    const [isRegistering, setIsRegistering] = React.useState(false);
    const registrationForm = auth.useRegistrationForm();

    const saveResults = async () => {
        if (results == null) {
            return;
        }
        setIsSaving(true);

        let profile = props.profile;

        if (profile == null) {
            const registrationResult = await server.core.rpc.register({
                email: registrationForm.values.email,
                with_notification: true,
                invite_code: "i am not a robot",
                recipe_uuid: null,
                source: "program",
                platform: "web",
            });

            if (registrationResult.type == "invalid") {
                registrationForm.processErrors(registrationResult.errors);
                setIsSaving(false);
                return;
            } else if (registrationResult.type != "success") {
                setIsSaving(false);
                return;
            }
            profile = registrationResult.data;
        }

        const result = await server.journal.workouts.create_program({
            uuid: crypto.randomUUID(),
            creation: {
                mode: "PROGRAM",
                type: "FIVE_THREE_ONE",
                data,
            },
            template: {
                visibility: "PRIVATE",
                description: "",
                name: "5/3/1",
            },
            exercise_maxes: maxesForms.map(({type, form}) => ({
                type: "AMRAP",
                exercise_type: {uuid: type},
                reps: form.values.reps!,
                weight: form.values.weight!,
            })),
            workout_groups: results.map((workoutGroup, groupIndex) => ({
                ...workoutGroup,
                name: `Week ${groupIndex + 1}`,
                description: "",
                uuid: crypto.randomUUID(),
                workouts: workoutGroup.workouts.map((workout) => ({
                    ...workout,
                    maxes: "EXISTING_MAXES",
                    mode: "NORMAL",
                    name: workout.name,
                    instructions: "",
                    group: workoutGroup.uuid,
                    questions: [],
                    sections: [
                        {
                            uuid: crypto.randomUUID(),
                            name: null,
                            copied_from: null,
                            instructions: "",
                            type: "BASIC",
                            duration: null,
                            rounds: null,
                            groups: workout.exercises.map(
                                (exercise, exerciseIndex) => ({
                                    uuid: crypto.randomUUID(),
                                    exercises: [
                                        {
                                            ...exercise.exercise,
                                            type: {
                                                uuid: exercise.exercise.type,
                                            },
                                            order: exerciseIndex,
                                            mode: "NORMAL",
                                            structure: "STRAIGHT",
                                            progression: null,
                                            workout: workout.uuid,
                                            instructions: "",
                                            video_uid: null,
                                            rest_duration: null,
                                            sets: exercise.sets.map((set) => ({
                                                ...set,
                                                progression: null,
                                                reps: parseInt(set.reps),
                                                duration_type: null,
                                                speed_type: null,
                                                status: "INCOMPLETE",
                                                rpe: null,
                                                instructions: "",
                                                weight_type: "VALUE",
                                                weight_percentage: null,
                                                distance_type: null,
                                                duration: null,
                                                distance: null,
                                                speed: null,
                                                incline: null,
                                                resistance: null,
                                                attributes: [],
                                            })),
                                        },
                                    ],
                                }),
                            ),
                        },
                    ],
                })),
            })),
            program_users: null,
        });

        if (result.type == "success") {
            if (props.profile == null) {
                registrationForm.reset();

                void (await analytics.registered(
                    profile.user.tracking_key,
                    "program",
                    "trial",
                ));
                window.location.href = "/workouts/program/";
            }

            router.push("workouts.program", {});
        }
    };

    React.useEffect(() => {
        // calculateResults();
        // setIsRegistering(true);
    }, []);

    const exercisesDialog = journeys.useExercisesDialog();

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-xl">Training Maxes</h2>
                <p>
                    Pick a weight and enter how many reps you can do. Strong, clean,
                    fast reps. Not grinders.
                </p>
            </div>

            <div
                className="
                    grid grid-cols-1 gap-4
                    sm:grid-cols-2
                "
            >
                {maxesForms.map((exercise) => (
                    <div
                        key={exercise.type}
                        className="rounded-lg bg-orange-100 px-4 py-3"
                    >
                        <h2 className="text-base">{exercise.name}</h2>
                        <div className="mt-1 flex items-center gap-2">
                            <forms.PField noLabel field={exercise.form.fields.weight} />
                            <Icon icon="custom-bolder-cross" />
                            <forms.PField
                                noLabel
                                field={exercise.form.fields.reps}
                                suffix={"reps"}
                            />
                        </div>
                    </div>
                ))}
            </div>

            <div>
                <h2 className="text-xl">Program Mode</h2>
                <div className="mt-2 flex gap-4">
                    <button
                        type="button"
                        onClick={() => setProgramMode("TEMPLATE")}
                        className={classNames(
                            "px-4 py-2 rounded-md font-semibold text-sm",
                            programMode === "TEMPLATE"
                                ? "bg-indigo-600 text-white"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300",
                        )}
                    >
                        5/3/1 Templates
                    </button>
                    <button
                        type="button"
                        onClick={() => setProgramMode("LINEAR")}
                        className={classNames(
                            "px-4 py-2 rounded-md font-semibold text-sm",
                            programMode === "LINEAR"
                                ? "bg-indigo-600 text-white"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300",
                        )}
                    >
                        Linear Progression
                    </button>
                </div>
            </div>

            <h2 className="text-xl">Settings</h2>

            {exercisesDialog.dialog}
            {programMode === "TEMPLATE" ? (
                <div className="space-y-6 rounded-lg bg-sky-100 px-4 py-3">
                    <div
                        className="
                            flex gap-2
                            *:flex-1
                        "
                    >
                        <forms.PField field={programSettingsForm.fields.template} />
                        <forms.PField field={programSettingsForm.fields.cycle_type} />
                    </div>
                    <div
                        className="
                            flex gap-2
                            *:flex-1
                        "
                    >
                        <forms.PField
                            field={programSettingsForm.fields.swap_exercises}
                        />
                        <forms.PField
                            field={programSettingsForm.fields.assistance_exercises}
                        />
                    </div>
                    <div
                        className="
                            flex gap-2
                            *:flex-1
                        "
                    >
                        <div>
                            <forms.PField
                                field={programSettingsForm.fields.deload_week}
                            />
                            <p className="mt-2 text-xs text-gray-800">
                                Add a deload week at the end of the cycle.
                            </p>
                        </div>
                        <div>
                            <forms.PField
                                field={programSettingsForm.fields.heavy_single}
                            />
                            <p className="mt-2 text-xs text-gray-800">
                                Add a training max single after the work sets.
                            </p>
                        </div>
                    </div>
                    <div
                        className={classNames("grid grid-cols-2 gap-4 md:grid-cols-4")}
                    >
                        {maxesForms.map((lift, index) => (
                            <div key={index} className="space-y-3">
                                <h3
                                    style={{color: "#181a1b"}}
                                    className="text-lg font-light"
                                >
                                    {lift.short ?? lift.name} Options
                                </h3>
                                {programSettingsForm.values.assistance_exercises && (
                                    <div className="space-y-1">
                                        <h4 className="text-sm font-normal">
                                            Assistance
                                        </h4>
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                const result = await exercisesDialog({
                                                    disableCreate: true,
                                                });

                                                if (result != null) {
                                                    setLiftsPerDay(
                                                        produce(
                                                            liftsPerDay,
                                                            (draftState) => {
                                                                draftState[index][0] =
                                                                    props.exercise_types.find(
                                                                        (e) =>
                                                                            e.id ==
                                                                            result[0]
                                                                                .id,
                                                                    )!;
                                                            },
                                                        ),
                                                    );
                                                }
                                            }}
                                            className="
                                                truncate rounded-sm bg-white px-4 py-1
                                                text-sm text-gray-900 shadow-xs ring-1
                                                ring-gray-300 ring-inset
                                                hover:bg-gray-50
                                            "
                                        >
                                            {liftsPerDay[index][0]!.name}
                                        </button>
                                    </div>
                                )}
                                <div className="space-y-1">
                                    <h4 className="text-sm font-normal">
                                        Smallest plate
                                    </h4>
                                    <div className="max-w-24">
                                        <forms.PField
                                            noLabel
                                            field={lift.form.fields.smallest_plate}
                                            suffix={suffix}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="space-y-6 rounded-lg bg-purple-100 px-4 py-3">
                    <div
                        className="
                            flex gap-2
                            *:flex-1
                        "
                    >
                        <div>
                            <h4 className="text-sm font-normal">Number of weeks</h4>
                            <forms.PField
                                noLabel
                                field={linearProgramSettingsForm.fields.weeks}
                            />
                        </div>
                    </div>

                    <div
                        className={classNames("grid grid-cols-2 gap-4 md:grid-cols-4")}
                    >
                        {linearLiftForms.map((lift, index) => (
                            <div key={index} className="space-y-3">
                                <h3
                                    style={{color: "#181a1b"}}
                                    className="text-lg font-semibold"
                                >
                                    {lift.name}
                                </h3>
                                <div className="space-y-1">
                                    <h4 className="text-sm font-normal">
                                        Progression/week
                                    </h4>
                                    <div className="max-w-24">
                                        <forms.PField
                                            noLabel
                                            field={
                                                lift.form.fields.progression_per_week
                                            }
                                            suffix={suffix}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <h4 className="text-sm font-normal">
                                        Smallest plate
                                    </h4>
                                    <div className="max-w-24">
                                        <forms.PField
                                            noLabel
                                            field={lift.form.fields.smallest_plate}
                                            suffix={suffix}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <h4 className="text-sm font-normal">Accessory</h4>
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            const result = await exercisesDialog({
                                                disableCreate: true,
                                            });

                                            if (result != null) {
                                                setLinearLiftsPerDay(
                                                    produce(
                                                        linearLiftsPerDay,
                                                        (draftState) => {
                                                            draftState[index][0] =
                                                                props.exercise_types.find(
                                                                    (e) =>
                                                                        e.id ==
                                                                        result[0].id,
                                                                )!;
                                                        },
                                                    ),
                                                );
                                            }
                                        }}
                                        className="
                                            truncate rounded-sm bg-white px-4 py-1
                                            text-sm text-gray-900 shadow-xs ring-1
                                            ring-gray-300 ring-inset
                                            hover:bg-gray-50
                                        "
                                    >
                                        {linearLiftsPerDay[index][0]!.name}
                                    </button>
                                </div>
                                <div className="space-y-1 text-xs text-gray-700">
                                    <p>
                                        Main: {linearSchemes[lift.mainScheme].sets}×
                                        {linearSchemes[lift.mainScheme].reps} @{" "}
                                        {new Decimal(
                                            linearSchemes[lift.mainScheme].percentage,
                                        )
                                            .times(100)
                                            .toString()}
                                        %
                                    </p>
                                    {lift.opposite != null && (
                                        <p>
                                            Supplemental ({lift.opposite}):{" "}
                                            {
                                                linearSchemes[lift.supplementalScheme]
                                                    .sets
                                            }
                                            ×
                                            {
                                                linearSchemes[lift.supplementalScheme]
                                                    .reps
                                            }{" "}
                                            @{" "}
                                            {new Decimal(
                                                linearSchemes[lift.supplementalScheme]
                                                    .percentage,
                                            )
                                                .times(100)
                                                .toString()}
                                            %
                                        </p>
                                    )}
                                    <p>
                                        Alternating: TM single (odd weeks) / AMRAP (even
                                        weeks)
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex gap-4">
                <button
                    type="submit"
                    className="
                        rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold
                        text-white shadow-xs
                        hover:bg-indigo-500
                        focus-visible:outline-2 focus-visible:outline-offset-2
                        focus-visible:outline-indigo-600 focus-visible:outline-solid
                        disabled:border-slate-200 disabled:bg-slate-50
                        disabled:text-slate-500 disabled:shadow-none
                    "
                    onClick={(event) => {
                        event.preventDefault();
                        calculateResults();
                    }}
                >
                    Calculate
                </button>
                <button
                    type="submit"
                    disabled={isSaving || results == null}
                    className="
                        rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold
                        text-white shadow-xs
                        hover:bg-indigo-500
                        focus-visible:outline-2 focus-visible:outline-offset-2
                        focus-visible:outline-indigo-600 focus-visible:outline-solid
                        disabled:border-slate-200 disabled:bg-slate-50
                        disabled:text-slate-500 disabled:shadow-none
                    "
                    onClick={(event) => {
                        event.preventDefault();
                        if (props.profile == null) {
                            setIsRegistering(true);
                        } else {
                            void saveResults();
                        }
                    }}
                >
                    {isSaving ? "Saving" : "Save"}
                </button>
            </div>
            {results != null && (
                <div className="flex flex-col gap-4" ref={resultsRef}>
                    <ContentModal
                        isOpen={isRegistering}
                        setOpen={(state) => setIsRegistering(state)}
                    >
                        <form
                            autoComplete="off"
                            noValidate
                            onSubmit={(event) => {
                                event.preventDefault();
                                void saveResults();
                            }}
                            className="flex flex-col gap-4 p-6"
                        >
                            <h2 className="text-xl whitespace-nowrap">
                                Sign up for Joy Health Tracker
                            </h2>
                            <typography.P>
                                Plan your workouts and nutrition. Track your progress.
                            </typography.P>
                            <forms.PField field={registrationForm.fields.email} />
                            <Register
                                isSubmitting={
                                    registrationForm.values.email == "" || isSaving
                                }
                            />

                            <typography.P small muted>
                                Joy is free for a month. Afterwards, it’s just 5 bucks a
                                month.
                            </typography.P>
                        </form>
                    </ContentModal>
                    {results.map((group, groupIndex) => (
                        <div key={groupIndex}>
                            <h3 className="mb-2 text-lg">Week {groupIndex + 1}</h3>
                            <div
                                className="
                                    grid grid-cols-2 gap-8 rounded-lg bg-purple-100 px-4
                                    py-3
                                    md:grid-cols-4 md:gap-4
                                "
                            >
                                {group.workouts.map((liftDay, liftDayIndex) => {
                                    return (
                                        <div
                                            key={liftDayIndex}
                                            className="flex flex-col gap-2"
                                        >
                                            {liftDay.exercises.map(
                                                (exercise, exerciseIndex) => (
                                                    <ExercisePreview
                                                        exerciseCategory="WEIGHT"
                                                        key={exerciseIndex}
                                                        isImperial={isImperial}
                                                        exercise={exercise}
                                                    />
                                                ),
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export const ProgramWizard = (props: {
    program: server.journal.workouts.ActiveProgram.output;
    next_workout: models["journal.workouts.NextWorkoutWrapper"] | null;
    scope: "client" | "consumer" | "manager";
    onSelectWorkout: (
        target: {user_id: number; key: string},
        selected: server.journal.workouts.Workout.output,
    ) => void;
    target: {user_id: number; key: string | number | null};
    onAlreadyHasWorkout?: (date: string) => void;
}) => {
    const templates = props.program.workout_groups
        .map((group) => group.workouts)
        .flat();
    const dispatch = useAppDispatch();

    const isComplete =
        templates.filter((template) => template.used_on == null).length == 0;

    const today = new Date();

    const selectWorkoutDialog = dialogs.useDialog<{selectedDate: Date}>({
        Content: (props) => (
            <div className="space-y-2 pb-4">
                <h2 className="mt-4 px-6 text-xl font-semibold">Select workout date</h2>
                <DayPicker
                    defaultMonth={today}
                    selected={today}
                    onDayClick={(selectedDate) => {
                        props.close({selectedDate});
                    }}
                />
            </div>
        ),
    });

    const selectProgramWorkout = async (
        workoutTemplate:
            | server.journal.workouts.Workout.output
            | server.journal.workouts.ActiveProgram.output["workout_groups"][number]["workouts"][number],
        key: string,
    ) => {
        const target = {
            ...props.target,
            key,
        };

        const result = await processResponse(
            server.journal.workouts.select_program_workout({
                source_workout: workoutTemplate.uuid,
                user_id: target.user_id,
                date: key,
            }),
        );

        if (result.type == "invalid") {
            props.onAlreadyHasWorkout?.(target.key);
        }

        if (result.type == "success") {
            if (result.data == "SUBSCRIPTION_REQUIRED") {
                dispatch({type: "NEEDS_SUBSCRIPTION"});
                router.push("profile.subscription", {});
                return;
            }
            void analytics.track("SELECTED_PROGRAM_WORKOUT", {
                program_workout_source:
                    props.target.key == null ? "workouts" : "journal",
            });
            props.onSelectWorkout(target, result.data);
        }
    };

    const pickDateOrStart = async (
        template:
            | server.journal.workouts.Workout.output
            | server.journal.workouts.ActiveProgram.output["workout_groups"][number]["workouts"][number],
    ) => {
        if (props.target.key != null) {
            void selectProgramWorkout(template, props.target.key.toString());
        } else {
            const result = await selectWorkoutDialog();

            if (result != null) {
                const key = dates.formatISO(result.selectedDate, {
                    representation: "date",
                });
                void selectProgramWorkout(template, key);
            }
        }
    };

    const previewDialog = dialogs.useDialog<
        unknown,
        {template: server.journal.workouts.Workout.output}
    >({
        Content: ({template, close}) => (
            <dialogs.HModal.Content
                title={<h2 className="text-lg font-semibold">{template.name}</h2>}
                actions={
                    <div className="flex gap-4">
                        <button
                            onClick={close}
                            type="button"
                            className="
                                order-2 rounded-md bg-white px-2 py-1.5 text-xs
                                font-semibold text-gray-900 shadow-xs ring-1
                                ring-gray-300 ring-inset
                                hover:bg-gray-50
                            "
                        >
                            Close
                        </button>
                        <typography.AsyncButton
                            onClick={() => pickDateOrStart(template)}
                            type="button"
                            className="
                                order-1 rounded-md bg-indigo-600 px-2 py-1.5 text-xs
                                font-semibold text-white shadow-xs
                                hover:bg-indigo-500
                                focus-visible:outline-2 focus-visible:outline-offset-2
                                focus-visible:outline-indigo-600
                                focus-visible:outline-solid
                            "
                        >
                            Start
                        </typography.AsyncButton>
                    </div>
                }
                content={
                    <div className="space-y-4">
                        {annotateWithExercises(template).exercises.map((exercise) => (
                            <div key={exercise.uuid} className="space-y-2">
                                <h2>{exercise.type.name}</h2>
                                <ExerciseSetsPreview exercise={exercise} />
                            </div>
                        ))}
                    </div>
                }
            />
        ),

        modal: dialogs.HModal,
    });

    return (
        <div className="flex flex-col gap-4">
            {previewDialog.dialog}
            {selectWorkoutDialog.dialog}
            <typography.ImportantAction
                showIf={
                    isComplete &&
                    (props.scope == "consumer" || props.program.type != "CUSTOM")
                }
                onClick={() => router.push("create_workout_program.index", {})}
                title="Programming complete"
                subtitle="Click to start a new cycle."
            />

            {props.program.workout_groups.map((group) => (
                <div key={group.uuid}>
                    <h2 className="mb-2 text-base font-semibold">{group.name}</h2>
                    <ul className="flex flex-col gap-4">
                        {group.workouts.map(({used_on, ...template}) => (
                            <li
                                key={template.uuid}
                                className={classNames(
                                    "col-span-1 rounded-lg bg-white border border-gray-200 shadow-xs relative",
                                    Card.isComplete,
                                )}
                            >
                                <div
                                    className="
                                        flex w-full items-center justify-between
                                        space-x-4 p-4
                                    "
                                >
                                    <div className="flex-1 truncate">
                                        <div className="flex items-center space-x-3">
                                            <h3
                                                className="
                                                    truncate text-sm font-medium
                                                    text-gray-900
                                                "
                                            >
                                                {template.name}
                                            </h3>
                                        </div>
                                        <p
                                            className="
                                                mt-1 truncate text-sm text-gray-500
                                            "
                                        >
                                            {template.exercises_count}{" "}
                                            {utils.pluralize(
                                                template.exercises_count,
                                                "exercise",
                                                "exercises",
                                            )}
                                            {used_on != null && (
                                                <span>
                                                    {" "}
                                                    on {utils.formatDate(used_on)}
                                                </span>
                                            )}
                                        </p>
                                    </div>

                                    {used_on != null ? (
                                        <>
                                            {props.scope != "manager" && (
                                                <button
                                                    type="button"
                                                    className="
                                                        rounded-md bg-white px-2 py-1.5
                                                        text-xs font-semibold
                                                        text-gray-900 shadow-xs ring-1
                                                        ring-gray-300 ring-inset
                                                        hover:bg-gray-50
                                                    "
                                                    onClick={() => {
                                                        router.push("journal.workout", {
                                                            key: used_on,
                                                        });
                                                    }}
                                                >
                                                    View
                                                </button>
                                            )}
                                            <div
                                                className="
                                                    mx-auto flex size-8 items-center
                                                    justify-center rounded-full
                                                    bg-green-100
                                                "
                                            >
                                                <CheckIcon
                                                    className="size-5 text-green-600"
                                                    aria-hidden="true"
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <typography.AsyncButton
                                                onClick={() =>
                                                    pickDateOrStart({
                                                        ...template,
                                                        used_on,
                                                    })
                                                }
                                                type="button"
                                                className="
                                                    rounded-md bg-indigo-600 px-2 py-1.5
                                                    text-xs font-semibold text-white
                                                    shadow-xs
                                                    hover:bg-indigo-500
                                                    focus-visible:outline-2
                                                    focus-visible:outline-offset-2
                                                    focus-visible:outline-indigo-600
                                                    focus-visible:outline-solid
                                                "
                                            >
                                                Start
                                            </typography.AsyncButton>
                                            <typography.AsyncButton
                                                onClick={async () => {
                                                    const result =
                                                        await server.journal.workouts.preview_program(
                                                            {
                                                                form: template.uuid,
                                                            },
                                                        );

                                                    if (result.type == "success") {
                                                        void previewDialog({
                                                            template: result.data,
                                                        });
                                                    } else {
                                                        alert(
                                                            "Error previewing program",
                                                        );
                                                    }
                                                }}
                                                type="button"
                                                className="
                                                    rounded-md bg-white px-2 py-1.5
                                                    text-xs font-semibold text-gray-900
                                                    shadow-xs ring-1 ring-gray-300
                                                    ring-inset
                                                    hover:bg-gray-50
                                                "
                                            >
                                                Preview
                                            </typography.AsyncButton>
                                            <div
                                                className="
                                                    mx-auto flex size-8 items-center
                                                    justify-center rounded-full
                                                    bg-gray-100
                                                "
                                            ></div>
                                        </>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
};
